import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Integration, IntegrationPlatform, IntegrationPlatformCustomField, IntegrationPlatformCustomFieldType, IntegrationCRMUser, SetupDataResponse, CustomFieldConfiguration, SetupDataChannel, CRMUserMapping, UserMapping, PlatformUserMapping, UpdateCRMUserMapping } from 'voxloud-types/dist/models/integrations';
import { CRMUserMappings } from 'voxloud-types/dist/models/integrations';
import { IntegrationsService } from 'src/app/domains/integrations/services/integrations.service';
import { catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { SelectTeamOutput } from 'src/app/shared/components/integration-select-team/integration-select-team.component';
import { CUSTOM_FIELDS_NAME, SetupDataValues } from 'src/app/domains/integrations/models/integrations.model';
import { UsersService } from 'src/app/domains/users/services';
import { Company, IPbxUser } from 'voxloud-types/dist/models';

@Component({
  selector: 'gdp-edit-integration-popup',
  templateUrl: './edit-integration-popup.component.html',
  styleUrls: ['./edit-integration-popup.component.scss']
})
export class EditIntegrationPopupComponent implements OnInit {
  @Output() onUpdateIntegration: EventEmitter<Integration> = new EventEmitter<Integration>();
  @Input() company: Company;
  public form: FormGroup;
  public integration: Integration;
  public customFields: IntegrationPlatformCustomField[];
  public loading: boolean;
  public customFieldsFormGroup: FormGroup;
  public crmUsers: IntegrationCRMUser[];
  public channels: SetupDataChannel[];
  public setupData: SetupDataResponse;
  public isEmptyCF: boolean;
  public setupDataError: boolean;
  public isSaving: boolean;
  public CUSTOM_FIELDS_NAME = CUSTOM_FIELDS_NAME;
  public mappings: CRMUserMapping[] = [];
  public users: IPbxUser[]
  public allAvailableUsers;
  public mappingsGroup = new FormControl([]);
  public DEFAULT_FIELD_TYPES = [
    'AUTOMATIC_CONTACT_CREATION',
    'DEFAULT_USER_ID', 'CHANNEL_ID',
    'TEAM_ID', 'TEAMS_CHANNEL_ID',
    'DEPARTMENT_ID',
    'WORK_TYPE_ID',
    'USER_MAPPING'
  ];


  constructor(
    private activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private integrationService: IntegrationsService,
    private notification: NotificationService,
    private userService: UsersService
  ) { }

  ngOnInit() {
    this.loading = true;
    forkJoin({
      platform: this.integrationService.getPlatformById(this.integration?.platform_id).pipe(catchError(() => { this.loading = false; return of(undefined) })),
      setupData: this.integrationService.getSetupData(this.integration?.id).pipe(catchError(() => { this.loading = false; this.setupDataError = true; return of(undefined) })),
      mappings: this.integrationService.getUserMappings(this.integration?.id).pipe(catchError(() => {this.loading = false; return of(undefined)})),
      pbxUsers: this.userService.getUsersFromPbxById(this.company?.pbxId).pipe(catchError(() => {this.loading = false; return of(undefined)}))
    }).subscribe((res: {
        platform: IntegrationPlatform,
        setupData: SetupDataResponse,
        mappings: CRMUserMappings,
        pbxUsers: IPbxUser[]
      }) => {
        this.customFields = res.platform.custom_fields;
        this.setupData = res.setupData;
        this.isEmptyCF = res.platform.custom_fields.length === 0;
        this.mappings = res.mappings?.user_mappings;
        this.crmUsers = res.setupData?.users;
        this.users = res.pbxUsers
        this.buildEditForm();
        this.allAvailableUsers = {availableUsers: this.users, availableCrmUsers: this.crmUsers};
        this.loading = false;
      },
        () => {
          this.loading = false;
        });
  }

  buildEditForm() {
    let custom_fields = {};

    this.form = this.fb.group({
      name: new FormControl<string>(
        this.integration.name,
        [Validators.required]
      )
    })

    this.customFields?.forEach(
      (f: IntegrationPlatformCustomField) => {
        let defaultValue = f.default_value || '';
        const value = this.integration.custom_fields.find(v => v.name === f.name)?.value || '';

        switch(f.name) {
          case 'DEFAULT_USER_ID':
            defaultValue = this.setupData?.users[0].external_id;
            break;

          case 'CHANNEL_ID':
          case 'DEPARTMENT_ID':
          case 'WORK_TYPE_ID':
            if(this.setupData) {
              let id = this.setupData[CUSTOM_FIELDS_NAME[f.name]].find(v => v.id === value)
              custom_fields[f.name] = new FormControl<SetupDataChannel>(
                id,
                Validators.required
              );
            }
            return;
          case 'USER_MAPPING':
            custom_fields[f.name] = new FormControl(this.mappings);
          return;
          case 'TEAM_ID':
            custom_fields[f.name] = new FormControl<SelectTeamOutput>(
              {
                TEAM_ID: this.integration.custom_fields.find(v => v.name === 'TEAM_ID')?.value,
                TEAMS_CHANNEL_ID: this.integration.custom_fields.find(v => v.name === 'TEAMS_CHANNEL_ID')?.value,
              },
              Validators.required
            )
            return;

          case 'TEAMS_CHANNEL_ID':
            return;
        }

        const result = value !== '' ? value : defaultValue;

        switch(f.field_type) {
          case IntegrationPlatformCustomFieldType.BOOLEAN:
            custom_fields[f.name] = new FormControl<boolean>(
              result !== '' ? JSON.parse(result) : false,
              Validators.required
            );
            break;
          case IntegrationPlatformCustomFieldType.TEXT:
          case IntegrationPlatformCustomFieldType.PASSWORD:
            custom_fields[f.name] = new FormControl<string>(
              result,
              Validators.required
            );
            break;
          case IntegrationPlatformCustomFieldType.ARRAY:
            //TODO: to be implemented
            break;
        }
      })

    this.form.addControl('custom_fields', new FormGroup({
      ...custom_fields
    }));

    this.customFieldsFormGroup = this.form.get('custom_fields') as FormGroup;
  }

  isFieldValid(name: string) {
    return (
      !this.form.get(name).valid && this.form.get(name).touched
    );
  }

  public selectValue(customField, value: SetupDataValues): void {
    this.form.get(`custom_fields.${customField}`).setValue(value);
  }

  controlSort(a: string, b: string) {
    return 0;
  }

  close() {
    this.activeModal.close();
  }

  save() {
    this.isSaving = true;
    let submitData = { ...this.form.value};
    let custom_fields = {...this.form.value?.custom_fields, ...this.form.value?.custom_fields?.TEAM_ID}
    if (custom_fields != undefined) {
      let customFieldsKeys = Object.keys(custom_fields);
      let currentKey = customFieldsKeys.find((key: string) => ['CHANNEL_ID', 'WORK_TYPE', 'DEPARTMENT'].includes(key));

      if (custom_fields.DEFAULT_USER_ID) {
        submitData.custom_fields.DEFAULT_USER_ID = custom_fields.DEFAULT_USER_ID.external_id ?? submitData.custom_fields.DEFAULT_USER_ID
      }

      if(['CHANNEL_ID', 'WORK_TYPE', 'DEPARTMENT'].includes(currentKey)) {
        submitData.custom_fields[currentKey] = custom_fields[currentKey].id;
      }

      submitData.custom_fields = customFieldsKeys.map((key) => {
        let field = this.integration.custom_fields.find((v) => v.name === key)
        if(key === 'AUTOMATIC_CONTACT_CREATION' && field === undefined) {
          return { name: key, value: false, configuration: CustomFieldConfiguration.CUSTOMER }
        } else {
          return { name: key, value: custom_fields[key].id, configuration: field.configuration }
        }
      })

      submitData.custom_fields = submitData.custom_fields.map(
        f => {
        if (f.name === 'USER_MAPPING') {
          f.value = 'managed by mapping API';
        }
        return f;
      })
    }

    const mappings = this.integration.custom_fields.find(f => f.name === 'USER_MAPPING');
    if(mappings) {
      forkJoin({
        mappings: this.integrationService.saveUserMappings(this.integration.id,
          (custom_fields.USER_MAPPING as unknown as any[]).map(m => ({
            user_id: m.user.id,
            platform_user_id: m.platform_user.external_id
          })) as UpdateCRMUserMapping[]
        ),
        integration: this.integrationService.updateIntegration(this.integration.id, submitData)
      }).subscribe(
        () => {
          this.onUpdateIntegration.emit();
          this.isSaving = false;
          this.close();
        },
        (err) => {
          if (err.status === 400) {
            this.notification.error('company.integrations.notifications.error.updated');
          } else {
            this.notification.error('common.errors.oops');
          }
          this.isSaving = false;
          this.close();
        }
      );
    } else {
      this.integrationService.updateIntegration(this.integration.id, submitData).subscribe(
        () => {
          this.onUpdateIntegration.emit();
          this.isSaving = false;
          this.close();
        },
        (err) => {
          if (err.status === 400) {
            this.notification.error('company.integrations.notifications.error.updated');
          } else {
            this.notification.error('common.errors.oops');
          }
          this.isSaving = false;
          this.close();
      })
    }
  }

}
